import { useState } from 'react';
import { subMinutes } from 'date-fns';
import { Grid, Typography } from '@mui/material';

import useInterval from '../../hooks/useInterval';

import { TraceHistorySample } from '../../types';

import TraceHistoryPlaybackControls from 'features/traceHistory/TraceHistoryPlaybackControls';
import { grey } from '@mui/material/colors';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { PlayStatus } from '../traceHistory/types';
import { formatInTimeZone } from 'date-fns-tz';

import SpectrumChart from 'features/spectrumChart/SpectrumChart';

interface Props {
  analyzerId: number;
  snapshotId: number;
  traceSample: TraceHistorySample | undefined;
  traceIndex: number;
  handleTraceIndexChanged: (newValue: any) => void;
  timeline: { time: string }[];
}

const WeatherTraceDisplay = ({
  analyzerId,
  snapshotId,
  traceSample,
  traceIndex,
  handleTraceIndexChanged,
  timeline,
}: Props) => {
  const { timeFormatNoSeconds, dateFormatWithZeros, timezone } =
    useUserDateTime();

  const [playTimerStatus, setPlayTimerStatus] = useState<PlayStatus>(
    PlayStatus.ForwardPaused
  );

  const handleTraceIndexChange = (event: any, newValue: number | number[]) => {
    if (typeof newValue === 'number') handleTraceIndexChanged(newValue);
  };

  const handlePlayClicked = () => {
    if (traceIndex < timeline.length - 1)
      setPlayTimerStatus(PlayStatus.Forward);
  };

  const handlePauseClicked = () => {
    setPlayTimerStatus(PlayStatus.ForwardPaused);
  };

  const handleForwardClicked = () => {
    if (traceIndex < timeline.length - 1) {
      handleTraceIndexChanged(traceIndex + 1);
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    } else return;
  };

  const handleBackwardClicked = () => {
    if (traceIndex > 0) {
      handleTraceIndexChanged(traceIndex - 1);
      setPlayTimerStatus(PlayStatus.ForwardPaused);
    } else return;
  };

  useInterval(
    () => {
      handleTraceIndexChanged(traceIndex + 1);
    },
    playTimerStatus !== PlayStatus.ForwardPaused ? 1000 : null
  );

  if (timeline.length === 0)
    return (
      <div style={{ maxWidth: '100%' }}>
        <Grid
          container
          direction='column'
          wrap='nowrap'
          style={{ height: '100%', maxWidth: '100%' }}
        >
          <Typography sx={{ p: '2rem' }} color={grey[500]}>
            No traces found for the selected conditions.
          </Typography>
        </Grid>
      </div>
    );

  return (
    <div style={{ maxWidth: '100%' }}>
      <Grid
        container
        direction='column'
        wrap='nowrap'
        style={{ height: '100%', maxWidth: '100%' }}
      >
        <Grid item xs={9}>
          {traceSample && (
            <SpectrumChart
              analyzerId={analyzerId}
              snapshotId={snapshotId}
              staticSnapshot={traceSample}
              staticTrace={traceSample.trace.split(',').map(Number)}
            />
          )}
        </Grid>
        {traceSample && (
          <Grid container item justifyContent='center'>
            <span>
              {formatInTimeZone(
                subMinutes(new Date(timeline[traceIndex].time), 15),
                timezone,
                `${dateFormatWithZeros} ${timeFormatNoSeconds}`
              ) +
                ' to ' +
                formatInTimeZone(
                  new Date(timeline[traceIndex].time),
                  timezone,
                  timeFormatNoSeconds
                )}
            </span>
          </Grid>
        )}
        <Grid container item justifyContent='center' alignContent='center'>
          <TraceHistoryPlaybackControls
            selectedTraceIndex={traceIndex}
            handleTraceIndexChange={handleTraceIndexChange}
            traceHistoryLength={timeline.length}
            handleBackwardClicked={handleBackwardClicked}
            handlePlayClicked={handlePlayClicked}
            handleForwardClicked={handleForwardClicked}
            handlePauseClicked={handlePauseClicked}
            handleJumpToNextAlarm={() => {}}
            sliderMarks={[]}
            playTimerInterval={1000}
            playTimerStatus={playTimerStatus}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default WeatherTraceDisplay;
